import Modal from 'Components/Modal/Modal';
import { FormItem, Label } from 'Constants/styles';
import {
  ChangeEvent,
  FunctionComponent,
  MouseEvent,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MdArrowBack } from 'react-icons/md';

import { FormControl, FormWrapper, MerchantListBox, StepBackBtn, VInput, VTextarea } from './styles';

type Props = {
  updatePayoutStatus: (merchantId: string, toggleStatus: boolean) => Promise<boolean>;
  isModalLoading: boolean;
  setShowModal: (showModal: boolean) => void;
};

const PayoutStatusUpdateModal = ({ updatePayoutStatus, isModalLoading, setShowModal }: Props): ReactElement => {
  const BackIcon = MdArrowBack as FunctionComponent;

  const [modalStep, setModalStep] = useState<number>(1);
  const [merchantList, setMerchantList] = useState<Array<{ merchantId: string; success: boolean; attempts: number }>>(
    [],
  );
  const [merchantUpdateLog, setMerchantUpdateLog] = useState<Array<string>>([]);
  const [merchantListString, setMerchantListString] = useState<string>('');
  const [newPayoutStatus, setNewPayoutStatus] = useState<boolean>(true);

  const cancelRef = useRef(false);

  const merchantListChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    setMerchantListString(event.target.value);
  }, []);

  const fileUploadSelected = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const input = event.target;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;

    if (!file) return;

    const text = await file.text();
    const mIds = text
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.startsWith('mcht_'));
    setMerchantListString(mIds.join(', '));
  }, []);

  const executeUpdate = useCallback(async () => {
    const tempMerchantList = merchantList.filter((merchant) => !merchant.success);

    for (let i = 0; i < tempMerchantList.length; i++) {
      if (cancelRef.current) break;
      setMerchantUpdateLog((prev) => [
        ...prev,
        `Updating payout status of merchant #${tempMerchantList[i].merchantId}... `,
      ]);
      const success = await updatePayoutStatus(tempMerchantList[i].merchantId, newPayoutStatus);
      setMerchantList((prev) =>
        prev.map((v) =>
          v.merchantId === tempMerchantList[i].merchantId ? { ...v, success, attempts: v.attempts + 1 } : v,
        ),
      );
      setMerchantUpdateLog((prev) => {
        const copy = [...prev];
        copy.pop();
        return [...copy, `${prev[prev.length - 1]} ${success ? '[Successful]' : '[Failed]'}`];
      });
    }
  }, [merchantList, newPayoutStatus, updatePayoutStatus]);

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (modalStep === 1) {
        if (merchantListString.trim().length) {
          setMerchantList(
            merchantListString
              .trim()
              .split(',')
              .map((merchantId) => merchantId.trim())
              .filter((v, i, a) => a.indexOf(v) === i && !!v)
              .map((merchantId) => ({ merchantId: merchantId.trim(), success: false, attempts: 0 })),
          );
        }
        setModalStep(2);
      }
      if (modalStep === 2) {
        setModalStep(3);
        executeUpdate();
      }
      if (modalStep === 3) {
        executeUpdate();
      }
    },
    [executeUpdate, merchantListString, modalStep],
  );

  const handleCancel = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.preventDefault();
      cancelRef.current = true;
      setShowModal(false);
    },
    [setShowModal],
  );

  const handleStepBack = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.preventDefault();
      setModalStep(modalStep - 1);
    },
    [modalStep],
  );

  const handleStatusSelectChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    setNewPayoutStatus(e.target.value === '1');
  }, []);

  const hasFailed = useMemo(() => {
    return merchantList.filter((merchant) => !merchant.success).length > 0;
  }, [merchantList]);

  return (
    <Modal
      title={'Set Merchant Payout Status'}
      cancelBtnText={'Cancel'}
      confirmBtnText={modalStep === 1 ? 'Next' : modalStep === 2 ? 'Update' : hasFailed ? 'Retry failed' : ''}
      handleCancel={handleCancel}
      handleSubmit={onSubmit}
      hideConfirmBtn={modalStep === 3 && (!hasFailed || isModalLoading)}
      disableConfirmBtn={isModalLoading || !merchantListString.trim()}
      isLoading={isModalLoading}
      large={false}
      testId={'create-marketplace-modal'}
    >
      <FormWrapper>
        {[2].includes(modalStep) && (
          <StepBackBtn onClick={(e) => handleStepBack(e)}>
            <BackIcon /> Back
          </StepBackBtn>
        )}
        {modalStep === 1 && (
          <>
            <FormItem>
              <Label>Merchant IDs (Comma separated)</Label>
              <VTextarea
                value={merchantListString}
                onChange={(e) => merchantListChange(e as ChangeEvent<HTMLTextAreaElement>)}
              />
            </FormItem>
            <FormItem>
              <Label>Extract from CSV file</Label>
              <VInput
                className="fileUpload"
                id="verif-docs-front-upload"
                type="file"
                accept={`.csv`}
                onChange={(e) => fileUploadSelected(e)}
              />
            </FormItem>
          </>
        )}
        {modalStep === 2 && (
          <>
            <FormItem>
              <Label>Merchants ({merchantList.length})</Label>
              <MerchantListBox>
                {merchantList.map((merchant) => (
                  <li>{merchant.merchantId}</li>
                ))}
              </MerchantListBox>
            </FormItem>
            <FormItem>
              <Label>Payout status</Label>
              <FormControl value={newPayoutStatus ? 1 : 0} onChange={(e) => handleStatusSelectChange(e)}>
                <option key="enable" value={1}>
                  Enable
                </option>
                <option key="disable" value={0}>
                  Disable
                </option>
              </FormControl>
            </FormItem>
          </>
        )}
        {modalStep === 3 && (
          <>
            <FormItem>
              <Label>Payout status: {newPayoutStatus ? 'Enabled' : 'Disabled'}</Label>
            </FormItem>
            <FormItem>
              <MerchantListBox>
                {merchantUpdateLog.map((entry) => (
                  <li>{entry}</li>
                ))}
              </MerchantListBox>
              <Label>
                Merchants (Queued: {merchantList.length} Successful:{' '}
                {merchantList.filter((merchant) => merchant.success).length} Failed:{' '}
                {merchantList.filter((merchant) => !merchant.success && merchant.attempts > 0).length})
              </Label>
            </FormItem>
          </>
        )}
      </FormWrapper>
    </Modal>
  );
};

export default PayoutStatusUpdateModal;
